/**
 * A promo code carried in a link (for example an email that points at
 * /courses/ai-trading-bootcamp?promo=LUMIWEALTH66).
 *
 * The buyer usually lands on a sales page first and only then clicks through
 * to checkout, whose own URL no longer has the code. So the code is remembered
 * for the browser tab and read back at checkout, where the server validates it
 * like any typed code. Rob, 2026-09-29: past students were emailed a code and
 * half of them (the Stripe-hosted checkout arm) had nowhere to type it.
 */
export const CHECKOUT_PROMO_CODE_STORAGE_KEY = 'botspot.checkout.promoCode';

const PROMO_QUERY_PARAMS = ['promo', 'promoCode', 'coupon'];
const PROMO_CODE_PATTERN = /^[A-Z0-9_-]{1,64}$/;

type CheckoutPromoCodeInput = string | null | undefined;

export function normalizeCheckoutPromoCode(value: CheckoutPromoCodeInput): string | null {
  if (!value) return null;
  const code = value.replace(/\s+/g, '').toUpperCase();
  return PROMO_CODE_PATTERN.test(code) ? code : null;
}

export function readPromoCodeFromSearch(search: string | null | undefined): string | null {
  const params = new URLSearchParams(search || '');
  for (const name of PROMO_QUERY_PARAMS) {
    const code = normalizeCheckoutPromoCode(params.get(name));
    if (code) return code;
  }
  return null;
}

function readStoredPromoCode(): string | null {
  try {
    return normalizeCheckoutPromoCode(
      window.sessionStorage.getItem(CHECKOUT_PROMO_CODE_STORAGE_KEY),
    );
  } catch {
    return null;
  }
}

/** Remember a code from the current URL for this tab. Returns the code, if any. */
export function captureCheckoutPromoCodeFromUrl(search: string | null | undefined): string | null {
  const code = readPromoCodeFromSearch(search);
  if (!code) return null;
  try {
    window.sessionStorage.setItem(CHECKOUT_PROMO_CODE_STORAGE_KEY, code);
  } catch {
    // Private mode or blocked storage: the code still works on this page.
  }
  return code;
}

/** The code for this checkout: one in the URL wins, then one remembered for the tab. */
export function getCheckoutPromoCode(search: string | null | undefined): string | null {
  return readPromoCodeFromSearch(search) || readStoredPromoCode();
}

export function rememberCheckoutPromoCode(code: string | null | undefined) {
  const normalized = normalizeCheckoutPromoCode(code);
  try {
    if (normalized) window.sessionStorage.setItem(CHECKOUT_PROMO_CODE_STORAGE_KEY, normalized);
    else window.sessionStorage.removeItem(CHECKOUT_PROMO_CODE_STORAGE_KEY);
  } catch {
    // Storage blocked: nothing to remember.
  }
}
